import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { creditApi, userApi, twoFactorApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { UserCircle, Mail, Coins, ArrowLeft, Crown, Loader2, Calendar, FileText, TrendingUp, CheckCircle2, ChevronDown, ChevronUp, Shield, AlertCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { PlanComparison } from '@/components/PlanComparison';

export function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [credits, setCredits] = useState<number | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [accountInfo, setAccountInfo] = useState<any>(null);
  const [accountInfoLoading, setAccountInfoLoading] = useState(true);
  const [usageData, setUsageData] = useState<any>(null);
  const [upgradingPlan, setUpgradingPlan] = useState(false);
  const [showDetailedComparison, setShowDetailedComparison] = useState(false);

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [showTestCodeForm, setShowTestCodeForm] = useState(false);
  const [testCodeSent, setTestCodeSent] = useState(false);
  const [testCode, setTestCode] = useState('');
  const [testCodeError, setTestCodeError] = useState('');
  const [testCodeSuccess, setTestCodeSuccess] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Fetch account info
    const fetchAccountInfo = async () => {
      try {
        const response = await userApi.fetchAccountInfo();
        if (response && !response.error) {
          setAccountInfo(response);
        }
      } catch (error) {
        console.error('Failed to fetch account info:', error);
        // Set default values if account info fails to load
        setAccountInfo({
          account_type: 'free',
          email: user.email || '',
          username: user.username
        });
      } finally {
        setAccountInfoLoading(false);
      }
    };

    // Fetch credits
    const fetchCredits = async () => {
      try {
        const response = await creditApi.getCreditBalance();
        if (response.success && response.remaining_credits !== undefined) {
          setCredits(response.remaining_credits);
        }
      } catch (error) {
        console.error('Failed to fetch credits:', error);
      } finally {
        setCreditsLoading(false);
      }
    };

    fetchAccountInfo();
    fetchCredits();
    fetchUsageData();
  }, [user, navigate]);

  // Fetch usage data for daily limits
  const fetchUsageData = async () => {
    try {
      const response = await userApi.fetchUsage();
      if (response) {
        setUsageData(response);
      }
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
      // Don't show error for usage data, just log it
    }
  };

  // Fetch 2FA status on mount
  useEffect(() => {
    const fetch2FAStatus = async () => {
      try {
        const result = await twoFactorApi.get2FAStatus();
        setTwoFactorEnabled(result.two_factor_enabled);
      } catch (error) {
        console.error('Failed to fetch 2FA status:', error);
      }
    };

    if (user) {
      fetch2FAStatus();
    }
  }, [user]);

  // Toggle 2FA
  const handleToggle2FA = async () => {
    setTwoFactorLoading(true);
    try {
      if (twoFactorEnabled) {
        await twoFactorApi.disable2FA();
        setTwoFactorEnabled(false);
        setShowTestCodeForm(false);
      } else {
        await twoFactorApi.enable2FA();
        setTwoFactorEnabled(true);
      }
    } catch (error) {
      console.error('Failed to toggle 2FA:', error);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  // Test 2FA by sending code
  const handleSendTestCode = async () => {
    try {
      await twoFactorApi.send2FACode();
      setTestCodeSent(true);
      setShowTestCodeForm(true);
    } catch (error) {
      console.error('Failed to send test code:', error);
      setTestCodeError('Failed to send test code');
    }
  };

  // Verify test code
  const handleVerifyTestCode = async () => {
    setTestCodeError('');
    setTestCodeSuccess(false);
    try {
      const result = await twoFactorApi.verify2FACode(testCode, false);
      if (result.success) {
        setTestCodeSuccess(true);
        setTestCodeError('');
        // Clear form after a delay to show success message
        setTimeout(() => {
          setShowTestCodeForm(false);
          setTestCode('');
          setTestCodeSent(false);
          setTestCodeSuccess(false);
        }, 3000);
      }
    } catch (error: any) {
      setTestCodeError(error.message || 'Verification failed');
      setTestCodeSuccess(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUpgradePlan = async () => {
    try {
      setUpgradingPlan(true);
      // Use Stripe API to handle subscription upgrade
      const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

      if (!stripe) {
        console.error('Stripe failed to load');
        return;
      }

      // Make API call to create checkout session
      const response = await userApi.createSubscriptionCheckout();

      if (response?.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.checkout_url;
      }
    } catch (error) {
      console.error('Error upgrading plan:', error);
    } finally {
      setUpgradingPlan(false);
    }
  };

  const getAccountTypeBadge = (accountType: string | undefined) => {
    if (!accountType) return null;

    const type = accountType.toLowerCase();
    let color = 'bg-gray-500';
    let label = accountType;

    if (type === 'free') {
      color = 'bg-gray-500';
      label = 'Free';
    } else if (type === 'pro') {
      color = 'bg-blue-500';
      label = 'Pro';
    } else if (type === 'premium') {
      color = 'bg-gradient-to-r from-amber-500 to-yellow-500';
      label = 'Premium';
    }

    return (
      <Badge className={`${color} text-white px-3 py-1 text-sm font-semibold`}>
        {type === 'premium' && <Crown className="w-3 h-3 mr-1 inline" />}
        {label}
      </Badge>
    );
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
            >
              Log Out
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {accountInfoLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Profile Information Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCircle className="w-5 h-5" />
                  Account Information
                </CardTitle>
                <CardDescription>
                  Your personal account details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* User Avatar and Name */}
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-white font-bold text-3xl">
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h2 className="text-2xl font-semibold text-gray-900">
                        {user.username}
                      </h2>
                      {getAccountTypeBadge(accountInfo?.account_type)}
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 mt-1">
                      <Mail className="w-4 h-4" />
                      <span>{accountInfo?.email || 'No email provided'}</span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Account Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Account Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600 mb-1">Username</div>
                      <div className="font-semibold text-gray-900">{user.username}</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600 mb-1">Email</div>
                      <div className="font-semibold text-gray-900">{accountInfo?.email || 'Not provided'}</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600 mb-1">Account Type</div>
                      <div className="font-semibold text-gray-900">
                        {accountInfo?.account_type ? (
                          <span className="capitalize">{accountInfo.account_type}</span>
                        ) : (
                          'Free'
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

          {/* Credits Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coins className="w-5 h-5" />
                Credits Balance
              </CardTitle>
              <CardDescription>
                Your available credits for AI-powered patent analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-gradient-to-br from-primary-light-4 to-white p-6 rounded-lg border-2 border-primary">
                <div className="text-center">
                  {creditsLoading ? (
                    <div className="text-gray-500">Loading credits...</div>
                  ) : credits !== null ? (
                    <>
                      <div className="text-5xl font-bold text-primary mb-2">
                        {credits.toLocaleString()}
                      </div>
                      <div className="text-gray-600 font-medium">Credits Remaining</div>
                    </>
                  ) : (
                    <div className="text-gray-500">Credits unavailable</div>
                  )}
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p>Credits are used for tasks like claims analysis, office action processing, and more.</p>
              </div>
            </CardContent>
          </Card>

          {/* Two-Factor Authentication Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Two-Factor Authentication
              </CardTitle>
              <CardDescription>
                Add an extra layer of security to your account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Display */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Status:</span>
                    <Badge className={twoFactorEnabled ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}>
                      {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </div>
                <Button
                  onClick={handleToggle2FA}
                  disabled={twoFactorLoading}
                  variant={twoFactorEnabled ? 'outline' : 'default'}
                  size="sm"
                >
                  {twoFactorLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  {twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA'}
                </Button>
              </div>

              {/* Test 2FA Section - Only shown when enabled */}
              {twoFactorEnabled && (
                <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                  <h4 className="font-medium text-gray-900">Test Two-Factor Authentication</h4>
                  <p className="text-sm text-gray-600">
                    Send a test verification code to your email to ensure 2FA is working correctly.
                  </p>

                  {!showTestCodeForm ? (
                    <Button
                      onClick={handleSendTestCode}
                      variant="outline"
                      size="sm"
                    >
                      Send Test Code
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Verification code sent to your email</span>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Enter 6-digit code
                        </label>
                        <Input
                          type="text"
                          value={testCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setTestCode(value);
                          }}
                          placeholder="000000"
                          maxLength={6}
                          className="font-mono text-center text-lg tracking-widest"
                        />
                      </div>

                      {testCodeSuccess && (
                        <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="font-medium">Test successful! 2FA is working correctly.</span>
                        </div>
                      )}

                      {testCodeError && !testCodeSuccess && (
                        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                          <AlertCircle className="w-4 h-4" />
                          <span>{testCodeError}</span>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button
                          onClick={handleVerifyTestCode}
                          disabled={testCode.length !== 6}
                          size="sm"
                        >
                          Verify Code
                        </Button>
                        <Button
                          onClick={() => {
                            setShowTestCodeForm(false);
                            setTestCode('');
                            setTestCodeSent(false);
                            setTestCodeError('');
                            setTestCodeSuccess(false);
                          }}
                          variant="outline"
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Information Section */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">How it works</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>When enabled, you'll receive a 6-digit verification code via email during login</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Verification codes expire after 15 minutes for security</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>You can trust devices for 30 days to skip verification on known devices</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Maximum 3 attempts per code to prevent unauthorized access</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Daily Onboarding Limits Card */}
          {usageData?.daily_onboarding && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Daily Onboarding Limits
                </CardTitle>
                <CardDescription>
                  Your daily patent/publication and document upload limits
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-gray-600">
                  Current tier: <span className="font-semibold capitalize text-gray-900">
                    {usageData.subscription_tier}
                  </span> • Resets daily at midnight
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Patents & Publications Limit */}
                  <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-800 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Patents & Publications
                      </h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        usageData.daily_onboarding.patent_publications.percentage_used >= 80
                          ? 'bg-red-100 text-red-800'
                          : usageData.daily_onboarding.patent_publications.percentage_used >= 60
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {usageData.daily_onboarding.patent_publications.percentage_used}% used
                      </span>
                    </div>

                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-semibold text-2xl text-gray-900">
                        {usageData.daily_onboarding.patent_publications.current}
                      </span>
                      <span className="text-gray-600">
                        / {usageData.daily_onboarding.patent_publications.limit}
                      </span>
                    </div>

                    <div className="w-full h-3 rounded-full bg-gray-200 mb-2">
                      <div
                        className={`h-3 rounded-full transition-all duration-300 ${
                          usageData.daily_onboarding.patent_publications.percentage_used >= 80
                            ? 'bg-red-500'
                            : usageData.daily_onboarding.patent_publications.percentage_used >= 60
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                        }`}
                        style={{
                          width: `${Math.min(100, usageData.daily_onboarding.patent_publications.percentage_used)}%`
                        }}
                      />
                    </div>

                    <div className="text-sm text-gray-600">
                      {usageData.daily_onboarding.patent_publications.remaining} remaining today
                    </div>
                  </div>

                  {/* Document Uploads Limit */}
                  <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-800 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Document Uploads
                      </h4>
                      {usageData.daily_onboarding.uploads.limit === 0 ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                          Not available
                        </span>
                      ) : (
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          usageData.daily_onboarding.uploads.percentage_used >= 80
                            ? 'bg-red-100 text-red-800'
                            : usageData.daily_onboarding.uploads.percentage_used >= 60
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {usageData.daily_onboarding.uploads.percentage_used}% used
                        </span>
                      )}
                    </div>

                    {usageData.daily_onboarding.uploads.limit === 0 ? (
                      <div className="text-center py-6">
                        <div className="text-sm text-gray-600 mb-2">
                          Upgrade to Pro or Premium for document uploads
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="font-semibold text-2xl text-gray-900">
                            {usageData.daily_onboarding.uploads.current}
                          </span>
                          <span className="text-gray-600">
                            / {usageData.daily_onboarding.uploads.limit}
                          </span>
                        </div>

                        <div className="w-full h-3 rounded-full bg-gray-200 mb-2">
                          <div
                            className={`h-3 rounded-full transition-all duration-300 ${
                              usageData.daily_onboarding.uploads.percentage_used >= 80
                                ? 'bg-red-500'
                                : usageData.daily_onboarding.uploads.percentage_used >= 60
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                            }`}
                            style={{
                              width: `${Math.min(100, usageData.daily_onboarding.uploads.percentage_used)}%`
                            }}
                          />
                        </div>

                        <div className="text-sm text-gray-600">
                          {usageData.daily_onboarding.uploads.remaining} remaining today
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upgrade Plan Card */}
          {accountInfo?.account_type && (
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  {accountInfo.account_type.toLowerCase() === 'premium' ? 'Your Plan' : 'Upgrade Your Plan'}
                </CardTitle>
                <CardDescription>
                  {accountInfo.account_type.toLowerCase() === 'premium'
                    ? 'Compare all available plans and features'
                    : 'Unlock more features and higher daily limits with a subscription upgrade'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Free Plan */}
                  <div className={`rounded-lg border-2 p-4 ${
                    accountInfo.account_type.toLowerCase() === 'free'
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 bg-white'
                  }`}>
                    <div className="text-center mb-3">
                      <h4 className="font-bold text-gray-900 mb-1">Free</h4>
                      <div className="text-2xl font-bold text-gray-900">$0</div>
                      <div className="text-xs text-gray-600">forever</div>
                    </div>
                    {accountInfo.account_type.toLowerCase() === 'free' && (
                      <div className="text-center mb-3 -mt-2">
                        <Badge className="bg-blue-100 text-blue-800 text-xs">Current Plan</Badge>
                      </div>
                    )}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">All core workflow features</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">MPEP, CFR, USC access</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Office action analysis</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Standard support</span>
                      </div>
                    </div>
                  </div>

                  {/* Pro Plan */}
                  <div className={`rounded-lg border-2 p-4 ${
                    accountInfo.account_type.toLowerCase() === 'pro'
                      ? 'border-purple-400 bg-purple-50'
                      : 'border-purple-300 bg-white shadow-md'
                  }`}>
                    <div className="text-center mb-3">
                      <h4 className="font-bold text-gray-900 mb-1">Pro</h4>
                      <div className="text-2xl font-bold text-purple-700">$19</div>
                      <div className="text-xs text-gray-600">per month</div>
                    </div>
                    {accountInfo.account_type.toLowerCase() === 'pro' && (
                      <div className="text-center mb-3 -mt-2">
                        <Badge className="bg-purple-100 text-purple-800 text-xs">Current Plan</Badge>
                      </div>
                    )}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">100 signup credits ($10 value)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">All Free features</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Full text search (MPEP/CFR)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">File upload for tasks & chat</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Foreign patents access</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Priority support</span>
                      </div>
                    </div>
                    {accountInfo.account_type.toLowerCase() === 'free' && (
                      <Button
                        onClick={handleUpgradePlan}
                        disabled={upgradingPlan}
                        className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
                        size="sm"
                      >
                        {upgradingPlan ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <TrendingUp className="w-3 h-3 mr-1" />
                        )}
                        Get Pro
                      </Button>
                    )}
                  </div>

                  {/* Premium Plan */}
                  <div className={`rounded-lg border-2 p-4 ${
                    accountInfo.account_type.toLowerCase() === 'premium'
                      ? 'border-pink-400 bg-pink-50'
                      : 'border-pink-300 bg-white shadow-lg'
                  }`}>
                    <div className="text-center mb-3">
                      <h4 className="font-bold text-gray-900 mb-1">Premium</h4>
                      <div className="text-2xl font-bold text-pink-700">$47</div>
                      <div className="text-xs text-gray-600">per month</div>
                    </div>
                    {accountInfo.account_type.toLowerCase() === 'premium' && (
                      <div className="text-center mb-3 -mt-2">
                        <Badge className="bg-pink-100 text-pink-800 text-xs">Current Plan</Badge>
                      </div>
                    )}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">1,000 signup credits ($100 value)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">All Pro features</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Saved drafts library</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Translation & PDF OCR</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">LaTeX conversion & batch processing</span>
                      </div>
                    </div>
                    {accountInfo.account_type.toLowerCase() !== 'premium' && (
                      <Button
                        onClick={handleUpgradePlan}
                        disabled={upgradingPlan}
                        className="w-full mt-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 whitespace-normal h-auto py-2"
                        size="sm"
                      >
                        {upgradingPlan ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 flex-shrink-0 animate-spin" />
                            <span>Processing...</span>
                          </>
                        ) : (
                          <>
                            <TrendingUp className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span>Get Premium</span>
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="text-xs text-gray-600 text-center">
                  All plans include unlimited access to onboarded documents and core features
                </div>

                {/* Detailed Comparison Toggle */}
                <div className="mt-6 text-center">
                  <Button
                    variant="outline"
                    onClick={() => setShowDetailedComparison(!showDetailedComparison)}
                    className="text-sm"
                  >
                    {showDetailedComparison ? (
                      <>
                        <ChevronUp className="w-4 h-4 mr-2" />
                        Hide Detailed Comparison
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-2" />
                        View Detailed Feature Comparison
                      </>
                    )}
                  </Button>
                </div>

                {/* Detailed Plan Comparison */}
                {showDetailedComparison && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <PlanComparison
                      currentPlan={accountInfo?.account_type?.toLowerCase() as 'free' | 'pro' | 'premium'}
                      onUpgrade={handleUpgradePlan}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          </div>
        )}
      </div>
    </div>
  );
}
